import { patchUpdateCellInfo, putTagToCell } from "@/apis/table/route";
import {
  GetCellsListResponse,
  GetTagsFromCellResponse,
  TagInfo,
} from "@/apis/table/types";
import { useSelectedTags, useTableId } from "@/components/mapping/hooks";
import {
  TagsSingleSelectedManager,
  useMappingSearchResults,
} from "@/components/mapping/retrieval-results/hooks";
import { tagKey } from "@/components/mapping/utils";
import { factAddress } from "@/jspreadsheet/utils";
import queries from "@/queries";
import { CellType, Fact, useMappingStore } from "@/stores/mapping";
import { AccountingPeriod, Accumulated, TargetYear } from "@/types/enums";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";

export const useMappingInfos = ({
  toToast = true,
  addressWithSheetId,
  onSuccess,
}: {
  toToast?: boolean;
  addressWithSheetId?: string | null;
  onSuccess?: () => void;
}) => {
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const { selectedMappedInfo, set: setSelectedTags } =
    useSelectedTags(addressWithSheetId);
  const {
    data: { workbookId, tableId },
    isLoading,
    isError,
  } = useTableId();
  const { dimension } = useMappingStore((state) => state.dimension);
  const setUpdatedAt = useMappingStore(
    (state) => state.mappedInfos.setUpdatedAt,
  );

  const {
    processedData: { tags: tagsRetrieved },
  } = useMappingSearchResults(dimension);

  const queryClient = useQueryClient();
  const getCurrentPeriodInfo = (fact?: Fact) => {
    const currentCellTags =
      workbookId && tableId && fact
        ? queryClient.getQueryData<GetTagsFromCellResponse>(
            queries
              .workbook(workbookId)
              ._ctx.tables(tableId)
              ._ctx.cells(fact.address)
              ._ctx.period().queryKey,
          )
        : undefined;

    return {
      accountingPeriod: currentCellTags?.period ?? null,
      targetYear: currentCellTags?.targetYear ?? null,
      accumulated: currentCellTags?.accumulated ?? null,
    };
  };

  const { mutateAsync, isPending } = useMutation({
    mutationKey: [
      "put-tag-to-cell",
      workbookId,
      tableId,
      selectedFact?.address,
    ],
    mutationFn: async (params: {
      tags: TagInfo[];
      workbookId: string;
      tableId: string;
      selectedFact: Fact;
      accountingPeriod: AccountingPeriod | null;
      targetYear: TargetYear | null;
      accumulated: Accumulated | null;
      onError?: () => void;
    }) => {
      const {
        workbookId,
        tableId,
        selectedFact,
        tags,
        accountingPeriod,
        targetYear,
        accumulated,
      } = params;

      if (!tagsRetrieved) return;

      const tagManager = new TagsSingleSelectedManager(tags);

      queryClient.cancelQueries({
        queryKey: queries
          .workbook(workbookId!)
          ._ctx.tables(tableId!)
          ._ctx.cells(selectedFact.address)
          ._ctx.tags().queryKey,
      });

      let tagsToSave: TagInfo[] = [];
      if (dimension === "LINE_ITEM") {
        const lineItemCurrentlyTagged = tags.filter(
          (t) => t.type === "LINE_ITEM",
        );
        if (lineItemCurrentlyTagged.length > 1) {
          tagsToSave.push(
            lineItemCurrentlyTagged[lineItemCurrentlyTagged.length - 1],
          );
          tagsToSave.push(...tags.filter((t) => t.type === "MEMBER"));
        } else {
          tagsToSave.push(...tags);
        }
      } else {
        const tagsByAxis = tagManager.getAxisTagsMap(
          tagsRetrieved,
          dimension,
          tags,
        );
        const tagsToRemove = tagManager.findTagsToRemove(tagsByAxis);
        tagsToSave = tags.filter((t) => !tagsToRemove.includes(t));
      }

      const lineItemTag =
        tagsToSave.find((tag) => tag.type === "LINE_ITEM") ?? null;
      const memberTags = tagsToSave.filter((tag) => tag.type === "MEMBER");

      const lineItemTagKey = lineItemTag
        ? tagKey(lineItemTag?.prefix, lineItemTag?.name)
        : null;
      const memberTagsKeys = memberTags.map((tag) =>
        tagKey(tag.prefix, tag.name),
      );

      const tagsToSet = lineItemTag
        ? [lineItemTag, ...memberTags]
        : [...memberTags];

      setSelectedTags({
        address: factAddress(selectedFact),
        mappingInfo: {
          tags: tagsToSet,
          accountingPeriod,
          targetYear,
          accumulated,
        },
      });
      queryClient.setQueryData(
        queries
          .workbook(workbookId!)
          ._ctx.tables(tableId!)
          ._ctx.cells(selectedFact.address)
          ._ctx.tags().queryKey,
        {
          tags: tagsToSet,
          period: accountingPeriod,
          targetYear,
          accumulated,
        },
      );

      try {
        await putTagToCell({
          workbookId,
          tableId,
          cellAddress: selectedFact.address,
          lineItemTag: lineItemTagKey,
          memberTags: memberTagsKeys,
          roleKey: lineItemTag?.roleKey ?? null,
          accountingPeriod,
          targetYear,
          accumulated,
        });

        queryClient.invalidateQueries({
          queryKey: queries
            .workbook(workbookId!)
            ._ctx.tables(tableId!)
            ._ctx.editablePreview().queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: queries
            .workbook(workbookId!)
            ._ctx.tables(tableId!)
            ._ctx.editableTreeView().queryKey,
        });

        if (toToast) {
          toast.success("저장 완료!", {
            className: "",
            duration: 1000,
          });
        }
      } catch (error) {
        toast.error("저장 실패", { className: "", duration: 1000 });
      }
      setUpdatedAt();
    },
    onSuccess: () => {
      setIsMapping(false);
      setIsMappingSuccess(true);

      onSuccess?.();
      setTimeout(() => {
        setIsMappingSuccess(false);
      }, 1000);
    },
    onSettled: () => {
      if (!workbookId || !tableId || !selectedFact) return;
      const count = queryClient.isMutating({
        mutationKey: [
          "put-tag-to-cell",
          workbookId,
          tableId,
          selectedFact.address,
        ],
      });
      if (count > 1) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: queries
          .workbook(workbookId)
          ._ctx.tables(tableId)
          ._ctx.cells(selectedFact.address)
          ._ctx.tags().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queries
          .workbook(workbookId)
          ._ctx.tables(tableId)
          ._ctx.taxonomy()
          ._ctx.tags("LINE_ITEM", selectedFact.address).queryKey,
      });
      if (dimension === "LINE_ITEM") {
        queryClient.invalidateQueries({
          queryKey: queries
            .workbook(workbookId)
            ._ctx.tables(tableId)
            ._ctx.taxonomy()
            ._ctx.tags("AXIS", selectedFact.address).queryKey,
        });
      }
    },
  });

  const { mutateAsync: updateCellInfo } = useMutation({
    mutationFn: async (params: {
      workbookId: string;
      tableId: string;
      selectedFact: Fact;
      cellType: CellType;
      definitionRoleKey: string | null;
    }) => {
      const { workbookId, tableId, selectedFact, cellType, definitionRoleKey } =
        params;
      return await patchUpdateCellInfo(
        workbookId!,
        tableId!,
        selectedFact.address,
        {
          type: cellType,
          definitionRoleKey: definitionRoleKey,
        },
      );
    },
  });
  const setIsMapping = useMappingStore(
    (state) => state.mappedInfos.mutation.mapping.set,
  );
  const setIsMappingSuccess = useMappingStore(
    (state) => state.mappedInfos.mutation.mapping.setIsMappingSuccess,
  );
  useEffect(() => {
    setIsMapping(isPending);
    if (isPending) {
      setIsMappingSuccess(false);
    }
  }, [isPending]);

  const handleAddTag =
    (
      tag: TagInfo,
      workbookId?: string | null,
      tableId?: string | null,
      selectedFact?: Fact,
      definitionRoleKey?: string | null,
    ) =>
    async () => {
      if (!workbookId || !tableId || !tagsRetrieved || !selectedFact) return;

      const { accountingPeriod, targetYear, accumulated } =
        getCurrentPeriodInfo(selectedFact);
      let oldTags: TagInfo[] = selectedMappedInfo.tags ?? [];
      if (tag.type === "LINE_ITEM") {
        oldTags = oldTags.filter(
          (t) => t.type !== "LINE_ITEM" && t.type !== "TEXTBLOCK",
        );
      }
      const tags = [...oldTags, tag];

      queryClient.setQueryData(
        queries.workbook(workbookId!)._ctx.tables(tableId!)._ctx.cells()
          .queryKey,
        (old: GetCellsListResponse | undefined) => {
          if (!old) return old;
          return {
            ...old,
            cells: old.cells.map((cell) => {
              if (cell.address === selectedFact.address) {
                return {
                  ...cell,
                  taggedInfos: {
                    ...cell.taggedInfos,
                    period: accountingPeriod,
                    targetYear,
                    accumulated,
                    roleKey: tag.roleKey,
                    tags: tags.map((tag) => ({
                      key: tagKey(tag.prefix, tag.name),
                      type: tag.type,
                    })),
                  },
                };
              }
              return cell;
            }),
          };
        },
      );
      if (definitionRoleKey) {
        await updateCellInfo({
          workbookId,
          tableId,
          selectedFact,
          cellType: selectedFact.cellType,
          definitionRoleKey,
        });
      }
      await mutateAsync({
        tags,
        workbookId,
        tableId,
        selectedFact,
        accountingPeriod,
        targetYear,
        accumulated,
      });
    };

  const handleRemoveTag =
    (
      tag: TagInfo,
      workbookId?: string | null,
      tableId?: string | null,
      selectedFact?: Fact,
    ) =>
    async () => {
      if (!workbookId || !tableId || !selectedFact) return;

      const { accountingPeriod, targetYear, accumulated } =
        getCurrentPeriodInfo(selectedFact);
      const tags = (selectedMappedInfo.tags ?? []).filter(
        (t) => t.name !== tag.name,
      );

      queryClient.setQueryData(
        queries.workbook(workbookId!)._ctx.tables(tableId!)._ctx.cells()
          .queryKey,
        (old: GetCellsListResponse | undefined) => {
          if (!old) return old;
          return {
            ...old,
            cells: old.cells.map((cell) => {
              if (cell.address === selectedFact.address) {
                return {
                  ...cell,
                  taggedInfos: {
                    ...cell.taggedInfos,
                    tags: tags.map((tag) => ({
                      key: tagKey(tag.prefix, tag.name),
                      type: tag.type,
                    })),
                  },
                };
              }
              return cell;
            }),
          };
        },
      );
      await mutateAsync({
        tags,
        workbookId,
        tableId,
        selectedFact,
        accountingPeriod,
        targetYear,
        accumulated,
      });
    };

  const handleSaveTags = async (
    tags: TagInfo[],
    workbookId?: string | null,
    tableId?: string | null,
    selectedFact?: Fact,
  ) => {
    if (!workbookId || !tableId || !selectedFact) return;

    const { accountingPeriod, targetYear, accumulated } =
      getCurrentPeriodInfo(selectedFact);
    await mutateAsync({
      tags,
      workbookId,
      tableId,
      selectedFact,
      accountingPeriod,
      targetYear,
      accumulated,
    });
  };

  const handleSetPeriod = (params: {
    workbookId: string | null;
    tableId: string;
    selectedFact: Fact;
    accountingPeriod?: AccountingPeriod | null;
    targetYear?: TargetYear | null;
    accumulated?: Accumulated | null;
  }) => {
    const {
      workbookId,
      tableId,
      selectedFact,
      accountingPeriod,
      targetYear,
      accumulated,
    } = params;

    if (!workbookId) return;
    const currentPeriodInfo = getCurrentPeriodInfo(selectedFact);

    mutateAsync({
      tags: selectedMappedInfo.tags ?? [],
      workbookId,
      tableId,
      selectedFact,
      accountingPeriod:
        accountingPeriod === undefined
          ? currentPeriodInfo.accountingPeriod
          : accountingPeriod,
      targetYear:
        targetYear === undefined ? currentPeriodInfo.targetYear : targetYear,
      accumulated:
        accumulated === undefined ? currentPeriodInfo.accumulated : accumulated,
    });
  };

  const addressToReturn = selectedFact ? factAddress(selectedFact) : null;

  return {
    address: addressWithSheetId ?? addressToReturn,
    selectedMappedInfo,
    tableId,
    workbookId,
    isLoading,
    isError,
    mutate: mutateAsync,
    handleAddTag,
    handleRemoveTag,
    handleSaveTags,
    handleSetPeriod,
    isMapping: isPending,
  };
};

export const useCellPeriod = ({
  workbookId,
  tableId,
  selectedFact,
}: {
  workbookId: string | null;
  tableId: string | null;
  selectedFact: Fact | null;
}) => {
  const {
    data: cellTags,
    isLoading,
    isFetching,
  } = useQuery({
    ...queries
      .workbook(workbookId!)
      ._ctx.tables(tableId!)
      ._ctx.cells(selectedFact?.address!)
      ._ctx.period(),
    enabled: !!workbookId && !!tableId && !!selectedFact?.address,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
  });
  return {
    cellTags,
    isLoading,
    isFetching,
  };
};

export const useUpdateCellPeriod = ({
  workbookId,
  tableId,
  selectedFact,
}: {
  workbookId: string | null;
  tableId: string | null;
  selectedFact: Fact | null;
}) => {
  const queryClient = useQueryClient();
  const addressWithSheetId = selectedFact ? factAddress(selectedFact) : null;
  const { cellTags } = useCellPeriod({
    workbookId,
    tableId: tableId!,
    selectedFact: selectedFact!,
  });

  const { selectedMappedInfo, set: setSelectedTags } =
    useSelectedTags(addressWithSheetId);
  const { mutateAsync: putPeriodToCell } = useMutation({
    mutationKey: [
      "put-period-to-cell",
      workbookId,
      tableId,
      selectedFact?.address,
    ],
    mutationFn: async (params: {
      period?: AccountingPeriod | null;
      targetYear?: TargetYear | null;
      accumulated?: Accumulated | null;
    }) => {
      if (!workbookId || !tableId || !selectedFact) return;
      if (
        !cellTags ||
        cellTags.period === undefined ||
        cellTags.targetYear === undefined ||
        cellTags.accumulated === undefined
      ) {
        throw new Error("Cell period is not found");
      }
      const { period, targetYear, accumulated } = params;
      const body = {
        period: period === undefined ? cellTags.period : period,
        targetYear: targetYear === undefined ? cellTags.targetYear : targetYear,
        accumulated:
          accumulated === undefined ? cellTags.accumulated : accumulated,
      };
      if (
        body.period === undefined &&
        body.targetYear === undefined &&
        body.accumulated === undefined
      )
        return;
      queryClient.setQueryData(
        queries
          .workbook(workbookId!)
          ._ctx.tables(tableId!)
          ._ctx.cells(selectedFact?.address!)
          ._ctx.period().queryKey,
        body,
      );
      setSelectedTags({
        address: addressWithSheetId!,
        mappingInfo: {
          tags: selectedMappedInfo.tags ?? [],
          accountingPeriod: body.period,
          targetYear: body.targetYear,
          accumulated: body.accumulated,
        },
      });
      // const response = await axiosInstance.put(
      //   `/v1/workbooks/${workbookId}/tables/${tableId}/cells/${selectedFact.address}/period`,
      //   body,
      // );
      const tags = selectedMappedInfo.tags ?? [];
      const lineItemTag = tags.find((tag) => tag.type === "LINE_ITEM") ?? null;
      const memberTags = tags.filter((tag) => tag.type === "MEMBER");

      const lineItemTagKey = lineItemTag
        ? tagKey(lineItemTag?.prefix, lineItemTag?.name)
        : null;
      const memberTagsKeys = memberTags.map((tag) =>
        tagKey(tag.prefix, tag.name),
      );
      await putTagToCell({
        workbookId,
        tableId,
        cellAddress: selectedFact.address,
        lineItemTag: lineItemTagKey,
        memberTags: memberTagsKeys,
        roleKey: lineItemTag?.roleKey ?? null,
        accountingPeriod: body.period,
        targetYear: body.targetYear,
        accumulated: body.accumulated,
      });
    },
    onError: () => {
      toast.error("저장 실패!");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queries
          .workbook(workbookId!)
          ._ctx.tables(tableId!)
          ._ctx.editablePreview().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queries
          .workbook(workbookId!)
          ._ctx.tables(tableId!)
          ._ctx.editableTreeView().queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: queries
          .workbook(workbookId!)
          ._ctx.tables(tableId!)
          ._ctx.cells(selectedFact?.address!)
          ._ctx.period().queryKey,
      });

      // 워크스페이스 셀 리스트 invalidate → period chip 재렌더
      queryClient.invalidateQueries({
        queryKey: queries
          .workbook(workbookId!)
          ._ctx.tables(tableId!)
          ._ctx.cells().queryKey,
      });
    },
  });
  const isPuttingPeriod =
    queryClient.isMutating({
      mutationKey: [
        "put-period-to-cell",
        workbookId,
        tableId,
        selectedFact?.address,
      ],
    }) > 0;
  return {
    putPeriodToCell,
    isPuttingPeriod,
  };
};
